var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { BaseSignerWalletAdapter, WalletConnectionError, WalletDisconnectionError, WalletError, WalletKeypairError, WalletNotConnectedError, WalletSignTransactionError, WalletWindowBlockedError, WalletWindowClosedError, } from '@solana/wallet-adapter-base';
import { Keypair } from '@solana/web3.js';
import OpenLogin, { OPENLOGIN_NETWORK } from '@toruslabs/openlogin';
import { getED25519Key } from '@toruslabs/openlogin-ed25519';
export class TorusWalletAdapter extends BaseSignerWalletAdapter {
    constructor(config) {
        super();
        this._options = Object.assign({ uxMode: 'popup', network: OPENLOGIN_NETWORK.MAINNET }, config.options);
        this._connecting = false;
        this._openLogin = null;
        this._keypair = null;
    }
    get publicKey() {
        var _a;
        return ((_a = this._keypair) === null || _a === void 0 ? void 0 : _a.publicKey) || null;
    }
    get ready() {
        return typeof window !== 'undefined';
    }
    get connecting() {
        return this._connecting;
    }
    get connected() {
        return !!this._keypair;
    }
    connect() {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                if (this.connected || this.connecting)
                    return;
                this._connecting = true;
                let openLogin;
                let privateKey;
                try {
                    openLogin = new OpenLogin(this._options);
                    yield openLogin.init();
                    privateKey = openLogin.privKey;
                    if (!privateKey) {
                        let listener;
                        try {
                            privateKey = yield new Promise((resolve, reject) => {
                                listener = ({ reason }) => {
                                    switch (reason === null || reason === void 0 ? void 0 : reason.message.toLowerCase()) {
                                        case 'user closed popup':
                                            reason = new WalletWindowClosedError(reason.message, reason);
                                            break;
                                        case 'unable to open window':
                                            reason = new WalletWindowBlockedError(reason.message, reason);
                                            break;
                                    }
                                    reject(reason);
                                };
                                window.addEventListener('unhandledrejection', listener);
                                openLogin.login().then(
                                // HACK: result.privKey is not padded to 64 bytes, use provider.privKey
                                (result) => resolve(openLogin.privKey), (reason) => listener({ reason }));
                            });
                        }
                        finally {
                            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                            window.removeEventListener('unhandledrejection', listener);
                        }
                    }
                }
                catch (error) {
                    if (error instanceof WalletError)
                        throw error;
                    throw new WalletConnectionError(error === null || error === void 0 ? void 0 : error.message, error);
                }
                let keypair;
                try {
                    keypair = Keypair.fromSecretKey(getED25519Key(privateKey).sk);
                }
                catch (error) {
                    throw new WalletKeypairError(error === null || error === void 0 ? void 0 : error.message, error);
                }
                this._openLogin = openLogin;
                this._keypair = keypair;
                this.emit('connect');
            }
            catch (error) {
                this.emit('error', error);
                throw error;
            }
            finally {
                this._connecting = false;
            }
        });
    }
    disconnect() {
        return __awaiter(this, void 0, void 0, function* () {
            const openLogin = this._openLogin;
            if (openLogin) {
                this._openLogin = null;
                this._keypair = null;
                try {
                    yield openLogin.logout();
                    yield openLogin._cleanup();
                }
                catch (error) {
                    this.emit('error', new WalletDisconnectionError(error === null || error === void 0 ? void 0 : error.message, error));
                }
                this.emit('disconnect');
            }
        });
    }
    signTransaction(transaction) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const keypair = this._keypair;
                if (!keypair)
                    throw new WalletNotConnectedError();
                try {
                    transaction.partialSign(keypair);
                }
                catch (error) {
                    throw new WalletSignTransactionError(error === null || error === void 0 ? void 0 : error.message, error);
                }
                return transaction;
            }
            catch (error) {
                this.emit('error', error);
                throw error;
            }
        });
    }
    signAllTransactions(transactions) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const keypair = this._keypair;
                if (!keypair)
                    throw new WalletNotConnectedError();
                try {
                    for (const transaction of transactions) {
                        transaction.partialSign(keypair);
                    }
                }
                catch (error) {
                    throw new WalletSignTransactionError(error === null || error === void 0 ? void 0 : error.message, error);
                }
                return transactions;
            }
            catch (error) {
                this.emit('error', error);
                throw error;
            }
        });
    }
}
//# sourceMappingURL=adapter.js.map